// Golden Thread — building information register (Phase 6)
import { Layers } from "lucide-react";
import RegisterScaffold from "@/components/RegisterScaffold";
import Pill from "@/components/Pill";

const STATUS_TONE = { Current: "green", Superseded: "neutral", Missing: "red", Requested: "amber" };

const config = {
    title: "Golden Thread",
    sectionTag: "Safety & Compliance",
    endpoint: "/golden-thread",
    testid: "goldenthread",
    moduleId: "goldenthread",
    icon: Layers,
    emptyHint: "No golden-thread information recorded for these buildings yet.",
    documentLink: true,
    documentLinkMarksCurrent: true,
    showCompleteness: true,
    guidance: (
        <>
            The Golden Thread is a <strong>register of the building information you're legally required to hold
            and keep current</strong> under the Building Safety Act — it's the index, not the filing cabinet.
            For each entry you record what the record is, its category, current version, who owns it, and its
            status (Current / Superseded / Missing / Requested). The value is in the <strong>Missing</strong> and{" "}
            <strong>Requested</strong> statuses — at any point you can show the regulator, or the client, exactly
            what information exists, what version it's at, who owns it, and what's still outstanding.
            <br /><br />
            Use <strong>Attach</strong> to upload the actual file (or link one already in the Document Library)
            directly onto an entry — this keeps the register and the library in sync instead of drifting apart.
            Attaching a file marks the entry Current; unlinking one drops it back to Missing so the gap stays visible.
        </>
    ),
    filters: [
        { param: "category", metaKey: "categories", allLabel: "All categories" },
        { param: "status", metaKey: "statuses", allLabel: "All statuses" },
    ],
    columns: [
        { key: "title", label: "Information", render: (r) => (<div><div className="font-semibold text-[#1F1F1F]">{r.title}</div>{r.description && <div className="text-[11px]" style={{ color: "#9A968F" }}>{r.description}</div>}</div>) },
        { key: "category", label: "Category" },
        { key: "building_id", label: "Building", render: (r, c) => c.buildingMap[r.building_id]?.name || "—" },
        { key: "version", label: "Version", render: (r) => r.version || "—" },
        { key: "owner", label: "Owner", render: (r) => r.owner || "—" },
        { key: "status", label: "Status", render: (r) => <Pill tone={STATUS_TONE[r.status] || "neutral"}>{r.status}</Pill> },
    ],
    fields: [
        { key: "title", label: "Information item", type: "text", required: true, full: true },
        { key: "category", label: "Category", type: "select", metaKey: "categories", required: true },
        { key: "status", label: "Status", type: "select", metaKey: "statuses", default: "Current" },
        { key: "version", label: "Version", type: "text" },
        { key: "owner", label: "Owner", type: "text" },
        { key: "source", label: "Source", type: "text" },
        { key: "last_reviewed", label: "Last reviewed", type: "date" },
        { key: "critical", label: "Critical (required before gateway/handover)", type: "checkbox" },
        { key: "portal_visible", label: "Visible to the client on their portal", type: "checkbox" },
        { key: "description", label: "Description", type: "textarea" },
    ],
};

export default function GoldenThreadPage() {
    return <RegisterScaffold config={config} />;
}
